// === WORKFLOW SUMMARY — premium totals card ===
// Moved verbatim from WorkflowSummaryScreen: the right-column premium card (header, the
// USE_BREAKDOWN_UI / classic-totals / loading branches and the Aanvragen CTA). All amounts
// and the CTA gating are composed in the SCREEN (next to the useOfferChain call) and passed
// in — this component only renders. QUIRK preserved on purpose: the strikethrough/discount
// branch keys on `packageDiscount && packageDiscount.amount > 0` (amount>0, not
// percentage>0.01), and the CTA greyed-out style keys on chainIncomplete && isLoadingRelation
// while disabled uses chainIncomplete || isLoadingRelation — hence two separate booleans.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatPrice, formatPhoneLabel, useConfig } from '@sluitstraat/core';
import { workflowSummaryStyles as styles } from '../../../styles/commonStyles';
import { PremiumBreakdownCard } from '../../../components/PremiumBreakdownCard';
import { USE_BREAKDOWN_UI } from '../../../config/uiFlags';

interface PremiumTotalsCardProps {
  showTaxSeparate: boolean;
  /** boolean | null: the screen's shouldShowDiscount() can yield null (packageDiscount && …). */
  showDiscount: boolean | null;
  packageDiscount: { percentage: number; amount: number; newMonthlyPremium: number } | null;
  monthlyPremium: number;
  originalPremiumTotal: number;
  subscriptionFee: number;
  prolongationFee: number;
  isConfigLoading: boolean;
  /** Offer chain in flight (relation → calculate → discount → quote): one status, no phases. */
  isChainLoading: boolean;
  /** The chain's EXISTING single error state (useOfferChain.relationError) — one catch covers
   *  the whole chain, so one error treatment covers both the relation and package calls. */
  chainError: string | null;
  ctaDisabled: boolean;
  ctaDisabledStyle: boolean;
  onConvert: () => void;
}

export const PremiumTotalsCard: React.FC<PremiumTotalsCardProps> = ({
  showTaxSeparate,
  showDiscount,
  packageDiscount,
  monthlyPremium,
  originalPremiumTotal,
  subscriptionFee,
  prolongationFee,
  isConfigLoading,
  isChainLoading,
  chainError,
  ctaDisabled,
  ctaDisabledStyle,
  onConvert,
}) => {
  const { t } = useTranslation();
  const { config } = useConfig();
  // Same contact source as the advisor card: the active brand's config —
  // an spd user must never see DFD's number.
  const contactPhone = config.customer?.contact?.phone?.main;
  const contactEmail = config.customer?.contact?.email;

  return (
    <div className="summary-premium-card" style={styles.premiumCard}>
      <div style={styles.premiumHeader}>
        <span style={styles.premiumTitle}>{t('screens.offerSummary.totalPremium')}</span>
      </div>

      {/* Chain status — lives in the cart, where the outcome lands. One in-flight
          label (premiums are already calculated by now, so no "berekenen" copy)
          or the single error treatment with the brand's own contact details. */}
      {chainError ? (
        <div style={styles.chainErrorBox} role="alert">
          {t('screens.offerSummary.chainErrorContact')}{' '}
          {contactPhone && (
            <a href={`tel:${contactPhone}`} style={styles.chainErrorLink}>{formatPhoneLabel(contactPhone)}</a>
          )}
          {contactPhone && contactEmail && <> {t('common.or', 'of')} </>}
          {contactEmail && (
            <a href={`mailto:${contactEmail}`} style={styles.chainErrorLink}>{contactEmail}</a>
          )}
        </div>
      ) : isChainLoading ? (
        <div style={styles.chainStatusRow}>
          <div style={styles.loadingPremiumSpinner}></div>
          <span>{t('screens.offerSummary.processingOffer')}</span>
        </div>
      ) : null}

      {/* Premium display — toggle between classic subtotal/discount layout and the itemised
          breakdown via USE_BREAKDOWN_UI. While the chain is in flight (or failed) the status
          row above is the single source of truth — the breakdown's own gate spinner would
          add a second spinner with the wrong label, so it is not rendered then. */}
      {USE_BREAKDOWN_UI ? (
        !isChainLoading && !chainError && <PremiumBreakdownCard showTaxSeparate={showTaxSeparate} />
      ) : showDiscount && !isConfigLoading ? (
        <div style={styles.premiumDisplayColumn}>
          {packageDiscount && packageDiscount.amount > 0 ? (
            <>
              <div style={styles.subtotalRow}>
                <span style={styles.subtotalText}>{t('common.subtotal')}</span>
                <span style={styles.subtotalText}>
                  € {formatPrice(originalPremiumTotal)}
                </span>
              </div>
              <div style={styles.discountRow}>
                <div style={styles.discountRowLeft}>
                  <svg style={styles.discountRowIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                    <line x1="7" y1="7" x2="7.01" y2="7"></line>
                  </svg>
                  <span style={styles.discountRowLabel}>
                    {t('common.packageDiscount')} ({formatPrice(packageDiscount.percentage)}%)
                  </span>
                </div>
                <span style={styles.discountRowValue}>
                  - € {formatPrice(originalPremiumTotal - packageDiscount.newMonthlyPremium)}
                </span>
              </div>
              <div style={styles.miniDivider}></div>
              <div style={styles.feeList}>
                <div style={styles.feeRow}>
                  <span style={styles.feeText}>{t('subscription.label')}</span>
                  <span style={styles.feeText}>€ {formatPrice(subscriptionFee)}</span>
                </div>
                <div style={styles.feeRow}>
                  <span style={styles.feeText}>{t('prolongation.label')}</span>
                  <span style={styles.feeText}>€ {formatPrice(prolongationFee)}</span>
                </div>
              </div>
              <div style={styles.divider}></div>
              <div style={styles.premiumAmountContainer}>
                <div style={styles.premiumAmountLarge}>
                  {`€ ${formatPrice(packageDiscount.newMonthlyPremium + subscriptionFee + prolongationFee)}`}
                </div>
                <div style={styles.premiumPeriodText}>{t('screens.offerSummary.perMonth')}</div>
                <div style={styles.taxNote}>{t('screens.offerSummary.includingInsuranceTax')}</div>
              </div>
            </>
          ) : (
            <>
              <div style={styles.subtotalRow}>
                <span style={styles.subtotalText}>{t('common.subtotal')}</span>
                <span style={styles.subtotalText}>
                  € {formatPrice(packageDiscount?.newMonthlyPremium || monthlyPremium)}
                </span>
              </div>
              <div style={styles.miniDivider}></div>
              <div style={styles.feeList}>
                <div style={styles.feeRow}>
                  <span style={styles.feeText}>{t('subscription.label')}</span>
                  <span style={styles.feeText}>€ {formatPrice(subscriptionFee)}</span>
                </div>
                <div style={styles.feeRow}>
                  <span style={styles.feeText}>{t('prolongation.label')}</span>
                  <span style={styles.feeText}>€ {formatPrice(prolongationFee)}</span>
                </div>
              </div>
              <div style={styles.divider}></div>
              <div style={styles.premiumAmountContainer}>
                <div style={styles.premiumAmountLarge}>
                  {`€ ${formatPrice((packageDiscount?.newMonthlyPremium || monthlyPremium) + subscriptionFee + prolongationFee)}`}
                </div>
                <div style={styles.premiumPeriodText}>{t('screens.offerSummary.perMonth')}</div>
                <div style={styles.taxNote}>{t('screens.offerSummary.includingInsuranceTax')}</div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div style={styles.loadingPremiumColumn}>
          <div style={styles.loadingPremiumSpinner}></div>
          <span style={styles.subtotalText}>{t('screens.offerSummary.loadingPremium')}</span>
        </div>
      )}

      {/* Aanvragen CTA — fixed margin below whichever premium variant rendered
          (breakdown / classic / loading); the card sizes to its content, so the
          button never gets pushed down by column-height differences. */}
      <button
        className="convert-button"
        onClick={onConvert}
        disabled={ctaDisabled}
        style={{
          ...styles.premiumCardCta,
          // Pre-existing quirk: greyed-out keys on chainIncomplete && loading, so a
          // failed chain left a solid-looking dead button. In the error state the
          // CTA is unmistakably disabled-looking as well.
          ...(ctaDisabledStyle || chainError ? styles.convertButtonDisabled : {}),
        }}
      >
        {t('screens.offerSummary.convertToRequest.button')}
        <svg style={styles.convertButtonIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12"></line>
          <polyline points="12 5 19 12 12 19"></polyline>
        </svg>
      </button>
    </div>
  );
};
